import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowUpRight } from "lucide-react";
import useAdminInstitution from "Explore/Hooks/useAdminInstitution";
import styles from "./ReportDetailLink.module.scss";
import classNames from "classnames";

/**
 * ReportDetailLink - link to the Report Detail page for a specific entity.
 * Only rendered when the current user is an admin.
 *
 * @param {Object} props
 * @param {Object} props.entity - The entity object { id, name, type, photo, avatar, ... }
 * @param {string} [props.variant="default"] - "default" | "light" | "outline"
 * @param {string} [props.label="View Benchmarking"] - Display text
 * @param {string} [props.className] - Additional class name
 * @param {boolean} [props.showIcon=true] - Whether to show the FileText icon
 * @param {boolean} [props.showArrow=true] - Whether to show the ArrowUpRight icon
 */
const ReportDetailLink = ({
  entity,
  variant = "default",
  label = "View Benchmarking",
  className,
  showIcon = true,
  showArrow = true,
}) => {
  const { isAdmin } = useAdminInstitution();

  if (!isAdmin || !entity?.id) {
    return null;
  }

  const entityType = entity.type || "institution";
  const toUrl = `/explore/report-detail?entity_id=${entity.id}&entity_type=${entityType}`;

  return (
    <Link
      to={toUrl}
      state={{
        entity: { ...entity, type: entityType },
        entityId: entity.id,
        entityType,
      }}
      className={classNames(
        styles["report-detail-link"],
        styles[`variant-${variant}`],
        className
      )}
      title={`View benchmarking for ${entity.name || "this entity"}`}
      data-testid="report-detail-link"
    >
      {showIcon && <FileText size={15} className={styles["icon"]} />}
      <span className={styles["text"]}>{label}</span>
      {showArrow && <ArrowUpRight size={14} className={styles["arrow"]} />}
    </Link>
  );
};

export default ReportDetailLink;
